import { FC, useState, useRef } from 'react'

import { Divider } from 'antd'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'

import { kFormatter } from 'src/Constants/functions'
import { MAX_MOBILE_WIDTH, MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'
import {
  useGetMetaAdsCampaignByIdQuery,
  useUpdateMetaAdsCampaignMutation,
} from 'src/Redux/adsCampaignApi'
import { useCheckoutEvents } from 'src/Hooks/useCheckoutEvents'
import { FIXED_BY } from 'src/Constants/constants'
import { PrimarySlider } from 'src/Components/PrimarySlider'
import { AutoRenewBlock } from 'src/Components/AutoRenewBlock'
import { DisableAutoRenewModal } from 'src/Components/CampaignDetailsAutoRenewBlock/DisableAutoRenewModal'

import {
  durationButtons,
  getInitialDays,
  getStrategyRanges,
  MAX_BUDGET,
  MIN_BUDGET,
  useCampaignPrice,
} from './helpers'
import { ChooseDurationButton } from './ChooseDurationButton'
import { useAutoRenew } from './useAutoRenew'

const Container = styled.div`
  display: flex;
  padding: 24px;
  margin-bottom: 70px;
  flex-direction: column;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    min-width: 400px;
  }
  @media screen and (max-width: ${MAX_TABLET_WIDTH}) {
    width: 100%;
  }

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 100%;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const SubTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  margin-bottom: 16px;
`

const Text = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  align-self: center;
  text-align: center;
`

const BudgetSliderWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`
const BudgetSliderSideContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const Blocks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`
const MoneyBlock = styled.div`
  display: flex;
  padding: 15px 16px 15px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const InnerMoneyBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  color: ${({ theme }) => theme.color.darkGrey};
  line-height: 150%;
`

const MatchingBlock = styled.div`
  display: flex;
  padding: 15px 16px 15px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.darkGrey};
`
const Value = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    gap: 0;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    padding: 8px 16px;
    background: ${({ theme }) => theme.color.white};
    border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  }
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 100%;
  }
`

const BlocksContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
  }
`
const StyledDivider = styled(Divider)`
  margin: 0;
  border-color: ${({ theme }) => theme.color.lightGrey};
`
export interface FormDataProps {
  country: string[]
  days: number
}

export const AdsBudget: FC = () => {
  const { handleCheckoutStartEvent } = useCheckoutEvents()
  const { number } = useParams()
  const { data, refetch, isFetching } = useGetMetaAdsCampaignByIdQuery(
    Number(number),
  )
  const [selectedDays, setSelectedDays] = useState<number | null>(null)
  const hasUserInteracted = useRef(false)
  const { rate, currencySymbol, valueSlider, setValueSlider } =
    useCampaignPrice(hasUserInteracted, data?.dailyBudget)

  const days = selectedDays ?? getInitialDays(data?.daysCount)
  const {
    autoRenew,
    isDisableModalOpen,
    closeDisableModal,
    handleAutoRenewChange,
    handleDisableConfirm,
  } = useAutoRenew(data?.id, data?.shouldAutoRenew)

  const handleSliderChange = (value: number): void => {
    setValueSlider(value)
    hasUserInteracted.current = true
  }

  const { reachRange, followersRange } = getStrategyRanges(
    data?.strategy,
    valueSlider,
  )

  const { setSearch } = useCustomSearchParams()

  const [updateMetaAdsCampaign, { isLoading: isUpdating }] =
    useUpdateMetaAdsCampaignMutation()

  const handleClick = async (): Promise<void> => {
    if (!data) {
      return
    }

    await updateMetaAdsCampaign({
      id: data?.id,
      dailyBudget: valueSlider,
      daysCount: days,
    })

    await refetch()

    setSearch({ step: ScheduleSteps.CHECKOUT })

    handleCheckoutStartEvent({
      platformName: CampaignPlatformTypeEnum.META,
      id: Number(data.id),
      campaignType: CampaignTypeEnum.BASIC,
      genres: data.genres.map((genre) => genre),
      startDate: (data.startDate || '').toString(),
      resultPrice: valueSlider * days,
    })
  }

  const hasTargetingSettings =
    Boolean(data?.countries.length) &&
    Boolean(data?.genres.length) &&
    Boolean(data?.languages.length)

  const hasCampaignSettings = hasTargetingSettings && Boolean(data?.visualType)

  const isContinueAvailable =
    hasCampaignSettings && Boolean(valueSlider) && Boolean(days)

  const matchingBlockText =
    data?.strategy === 'playlist'
      ? 'Estimated daily playlist followers'
      : 'Estimated daily track clicks'

  return (
    <Container>
      <Title>Set up campaign budget</Title>

      <div>
        <SubTitle>Choose duration</SubTitle>
        <BlocksContainer>
          {durationButtons.map(({ value, text, labelText }) => (
            <ChooseDurationButton
              key={value}
              selected={days === value}
              onClick={() => setSelectedDays(value)}
              text={text}
              labelText={labelText}
            />
          ))}
        </BlocksContainer>
      </div>
      <AutoRenewBlock checked={autoRenew} onChange={handleAutoRenewChange} />
      <DisableAutoRenewModal
        isOpen={isDisableModalOpen}
        onClose={closeDisableModal}
        onConfirm={handleDisableConfirm}
      />
      <div>
        <SubTitle>Choose daily budget</SubTitle>
        <BudgetSliderWrapper>
          <PrimarySlider
            min={MIN_BUDGET}
            max={MAX_BUDGET}
            value={valueSlider}
            onChange={handleSliderChange}
            step={MIN_BUDGET}
            formatValue={(val) =>
              `${currencySymbol}${Math.round(val * rate).toLocaleString('en-US')}`
            }
          />

          <BudgetSliderSideContainer>
            <Blocks>
              <MoneyBlock>
                <InnerMoneyBlock>
                  <span>Daily budget</span>
                  <span>{`$${valueSlider.toFixed(FIXED_BY)}`}</span>
                </InnerMoneyBlock>
                <StyledDivider variant='dashed' dashed />
                <InnerMoneyBlock>
                  <span>Total budget</span>
                  <span>{`$${(valueSlider * days).toFixed(FIXED_BY)}`}</span>
                </InnerMoneyBlock>
              </MoneyBlock>

              <MatchingBlock>
                <div>Estimated daily reach</div>
                <Value>{`${kFormatter(reachRange[0])} - ${kFormatter(reachRange[1])}`}</Value>
              </MatchingBlock>

              <MatchingBlock>
                <div>{matchingBlockText}</div>
                <Value>{`${followersRange[0].toLocaleString()} - ${followersRange[1].toLocaleString()}`}</Value>
              </MatchingBlock>
            </Blocks>
          </BudgetSliderSideContainer>
        </BudgetSliderWrapper>
      </div>

      {isContinueAvailable && (
        <ButtonContainer>
          <ButtonWrapper>
            <StyledCTAButton
              onClick={handleClick}
              loading={isUpdating || isFetching}
            >
              Continue
            </StyledCTAButton>
            <Text>You won`t be charged yet</Text>
          </ButtonWrapper>
        </ButtonContainer>
      )}
    </Container>
  )
}
